import { CircularProgress, Grid, useMediaQuery, useTheme } from '@mui/material';
import { Button } from '@mui/material';
import { useMemo } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldSetWrapper } from '~/components/GenericForm/FieldSetWrapper';
import {
  EntityAttribute,
  EntityAttributes,
  useEntityAttributes,
} from '~/data/attributes';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { useIncrementCount } from '~/hooks/useIncrementCount/useIncrementCount';
import { EditCustomDataFields } from '~/legacy-components/CustomDataFields';

interface EmploymentDetailsCustomDataProps {
  control: Control<Form>;
  index: number;
  pendingProposal?: boolean;
}

type SequenceAttribute = {
  sequence: number;
};

type OrderedEntityAttribute = EntityAttribute & SequenceAttribute;

type RenderCustomFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  index: number;
  fieldName: string;
  fieldType: string;
  label: string;
  enumOptionsKey?: string;
  disabled?: boolean;
};

const RenderControlledCustomField = <TFieldValues extends FieldValues>(
  props: RenderCustomFieldProps<TFieldValues>,
) => {
  const {
    control,
    index,
    fieldName,
    fieldType,
    label,
    enumOptionsKey,
    disabled,
  } = props;

  const formFieldName = `schedule.${index}.employmentDetails.customData.${fieldName}`;

  const { allEnums } = useAllEnums();

  const hasOptions =
    !!enumOptionsKey &&
    !!allEnums[enumOptionsKey] &&
    allEnums[enumOptionsKey].length > 0;
  const enumOptions: Enum[] = hasOptions ? allEnums[enumOptionsKey] : [];

  return (
    <EditCustomDataFields
      name={label}
      inputProps={{
        options: enumOptions,
        name: formFieldName,
        fullWidth: true,
        disabled,
      }}
      type={fieldType}
      control={control as Control<FieldValues>}
      shrinkLabel={true}
    />
  );
};

export const EmploymentDetailsCustomData = ({
  control,
  index,
  pendingProposal,
}: EmploymentDetailsCustomDataProps) => {
  const { t } = useTranslation();

  const { allAttributes, isLoading } = useEntityAttributes();
  const emplCustomDataFields = useMemo((): EntityAttributes => {
    return allAttributes ?? {};
  }, [allAttributes]);

  const emplDetailsLength =
    (Array.isArray(emplCustomDataFields) && emplCustomDataFields.length) || 0;

  const theme = useTheme();
  const isViewSizeMd = useMediaQuery(theme.breakpoints.between('md', 'lg'), {
    noSsr: true,
  });

  const showFieldsIncrement = isViewSizeMd ? 3 : 4;
  const showFields = useIncrementCount({
    increment: showFieldsIncrement,
    minCount: 1,
    maxCount: Math.ceil(emplDetailsLength / showFieldsIncrement),
  });

  const { getEnums } = useAllEnums();
  const { CUSTOM_DATA_FIELDS } = useMemo(() => {
    return {
      CUSTOM_DATA_FIELDS: getEnums(
        EnumListKey.CUSTOM_ENTITY_EMPLOYMENT_DETAILS,
      ),
    };
  }, [getEnums]);

  //@ts-ignore
  //eslint-disable-next-line
  const emplCustomDataFieldsArray = emplCustomDataFields as Array;

  const emplOrderedCustomDataFields: OrderedEntityAttribute[] =
    emplCustomDataFieldsArray
      ?.map((field: EntityAttribute): OrderedEntityAttribute => {
        const foundEnum = CUSTOM_DATA_FIELDS?.find(
          (customField) =>
            customField?.code === field?.fCustomDataAttributeName,
        );
        const sequence = foundEnum?.seq || 0;
        // Quikcfix: Apply translated label, instead of untranslated term.
        // TODO: EntityAttribute should probably be fixed in BE to provide the translated label.
        const translatedLabel =
          foundEnum?.name || field?.fCustomDataAttributeLabel;

        return {
          ...field,
          sequence,
          fCustomDataAttributeLabel: translatedLabel,
        };
      })
      .sort(
        (a: OrderedEntityAttribute, b: OrderedEntityAttribute) =>
          a.sequence - b.sequence,
      )
      ?.slice(0, showFields.value);

  if (isLoading) {
    return <CircularProgress size={50} />;
  }

  return (
    <FieldSetWrapper
      label={
        Array.isArray(emplCustomDataFields) && emplCustomDataFields.length > 0
          ? `${t('laCustomFields')} - ${
              showFields.value > emplDetailsLength
                ? emplDetailsLength
                : showFields.value
            } / ${emplDetailsLength}`
          : undefined
      }
    >
      <Grid container columnSpacing={2} rowSpacing={2}>
        {emplOrderedCustomDataFields?.map(
          (field: EntityAttribute, fieldIndex) => {
            return (
              <Grid
                item
                key={`renderedField-${fieldIndex}-fragment`}
                xs={12}
                sm={6}
                md={4}
                lg={3}
              >
                <RenderControlledCustomField
                  key={`renderedField-${fieldIndex}-new`}
                  control={control}
                  index={index}
                  fieldName={field.fCustomDataAttributeName}
                  fieldType={field.fCustomDataAttributeDataType}
                  label={field.fCustomDataAttributeLabel}
                  enumOptionsKey={field.fCustomDataAttributeDataSubtype}
                  disabled={pendingProposal}
                />
              </Grid>
            );
          },
        )}
        <Grid item xs={12}>
          {showFields.maxValue! > showFields.value && (
            <Button
              color="primary"
              fullWidth
              onClick={() => showFields.increase()}
              data-testid="customdata-show-more-button"
            >
              {t('showMore')}
            </Button>
          )}
          {showFields.value > showFields.minValue! && (
            <Button
              color="primary"
              fullWidth
              onClick={() => showFields.decrease()}
              data-testid="customdata-show-less-button"
            >
              {t('showLess')}
            </Button>
          )}
        </Grid>
      </Grid>
    </FieldSetWrapper>
  );
};
